import { ArrowRightOutlined } from '@ant-design/icons';
import { Col, Divider, Row } from 'antd';
import Text from 'antd/lib/typography/Text';
import { Channels } from '../../../common/channelsEnum';
import {
  HOUR_OPTIONS,
  WEEKLY_OPTIONS,
  MONTHLY_OPTIONS,
  CHANNEL_OPTION_LABELS
} from './labels';
import {
  DeliveryOptions,
  InappWebDeliveryOptions,
  Notification
} from '../../../../interfaces/backend/Notifications';

interface Comparison {
  channel: Channels;
  id: string;
  initialValue: string | boolean | number | undefined;
  value: string | boolean | number | undefined;
}

export interface ChannelDeliveryOptionsParams {
  channel: Channels;
  notification: Notification;
  updatedNotification: Notification;
}

const ChannelOptionsConfirm: React.FC<ChannelDeliveryOptionsParams> = ({
  channel,
  notification,
  updatedNotification
}: ChannelDeliveryOptionsParams) => {
  const getModalText = () => {
    const comparisons: Comparison[] =
      channel === Channels.EMAIL
        ? [
            {
              channel: Channels.EMAIL,
              id: 'enabled',
              initialValue: notification.channels.includes(Channels.EMAIL),
              value: updatedNotification.channels.includes(Channels.EMAIL)
            },
            {
              channel: Channels.EMAIL,
              id: 'instant.enabled',
              initialValue: (notification.options?.EMAIL as DeliveryOptions)
                .instant?.enabled,
              value: (updatedNotification.options?.EMAIL as DeliveryOptions)
                .instant?.enabled
            },
            {
              channel: Channels.EMAIL,
              id: 'hourly.enabled',
              initialValue: (notification.options?.EMAIL as DeliveryOptions)
                .hourly?.enabled,
              value: (updatedNotification.options?.EMAIL as DeliveryOptions)
                .hourly?.enabled
            },
            {
              channel: Channels.EMAIL,
              id: 'daily.enabled',
              initialValue: (notification.options?.EMAIL as DeliveryOptions)
                .daily?.enabled,
              value: (updatedNotification.options?.EMAIL as DeliveryOptions)
                .daily?.enabled
            },
            {
              channel: Channels.EMAIL,
              id: 'daily.hour',
              initialValue: (notification.options?.EMAIL as DeliveryOptions)
                .daily?.hour,
              value: (updatedNotification.options?.EMAIL as DeliveryOptions)
                .daily?.hour
            },
            {
              channel: Channels.EMAIL,
              id: 'weekly.enabled',
              initialValue: (notification.options?.EMAIL as DeliveryOptions)
                .weekly?.enabled,
              value: (updatedNotification.options?.EMAIL as DeliveryOptions)
                .weekly?.enabled
            },
            {
              channel: Channels.EMAIL,
              id: 'weekly.hour',
              initialValue: (notification.options?.EMAIL as DeliveryOptions)
                .weekly?.hour,
              value: (updatedNotification.options?.EMAIL as DeliveryOptions)
                .weekly?.hour
            },
            {
              channel: Channels.EMAIL,
              id: 'weekly.day',
              initialValue: (notification.options?.EMAIL as DeliveryOptions)
                .weekly?.day,
              value: (updatedNotification.options?.EMAIL as DeliveryOptions)
                .weekly?.day
            },
            {
              channel: Channels.EMAIL,
              id: 'monthly.enabled',
              initialValue: (notification.options?.EMAIL as DeliveryOptions)
                .monthly?.enabled,
              value: (updatedNotification.options?.EMAIL as DeliveryOptions)
                .monthly?.enabled
            },
            {
              channel: Channels.EMAIL,
              id: 'monthly.hour',
              initialValue: (notification.options?.EMAIL as DeliveryOptions)
                .monthly?.hour,
              value: (updatedNotification.options?.EMAIL as DeliveryOptions)
                .monthly?.hour
            },
            {
              channel: Channels.EMAIL,
              id: 'monthly.date',
              initialValue: (notification.options?.EMAIL as DeliveryOptions)
                .monthly?.date,
              value: (updatedNotification.options?.EMAIL as DeliveryOptions)
                .monthly?.date
            },
            {
              channel: Channels.EMAIL,
              id: 'off.enabled',
              initialValue: (notification.options?.EMAIL as DeliveryOptions).off
                ?.enabled,
              value: (updatedNotification.options?.EMAIL as DeliveryOptions).off
                ?.enabled
            },

            // There was a typo that was pushed to production that resulted in 'defaultDeliverOption'. The below code handles those notifications as well
            {
              channel: Channels.EMAIL,
              id: 'defaultDeliveryOption',
              initialValue:
                (notification.options?.EMAIL as DeliveryOptions)
                  .defaultDeliveryOption ||
                (notification.options?.EMAIL as DeliveryOptions)
                  .defaultDeliveryOption,
              value:
                (updatedNotification.options?.EMAIL as DeliveryOptions)
                  .defaultDeliveryOption ||
                (notification.options?.EMAIL as DeliveryOptions)
                  .defaultDeliveryOption
            }
          ]
        : [
            {
              channel: Channels.INAPP_WEB,
              id: 'enabled',
              initialValue: notification.channels.includes(Channels.INAPP_WEB),
              value: updatedNotification.channels.includes(Channels.INAPP_WEB)
            },
            {
              channel: Channels.INAPP_WEB,
              id: 'instant.enabled',
              initialValue: (
                notification.options?.INAPP_WEB as InappWebDeliveryOptions
              ).instant?.enabled,
              value: (
                updatedNotification.options
                  ?.INAPP_WEB as InappWebDeliveryOptions
              ).instant?.enabled
            },
            {
              channel: Channels.INAPP_WEB,
              id: 'instant:batching',
              initialValue: (
                notification.options?.INAPP_WEB as InappWebDeliveryOptions
              ).instant?.batching,
              value: (
                updatedNotification.options
                  ?.INAPP_WEB as InappWebDeliveryOptions
              ).instant?.batching
            },
            {
              channel: Channels.INAPP_WEB,
              id: 'instant:batchingKey',
              initialValue: (
                notification.options?.INAPP_WEB as InappWebDeliveryOptions
              ).instant?.batchingKey,
              value: (
                updatedNotification.options
                  ?.INAPP_WEB as InappWebDeliveryOptions
              ).instant?.batchingKey
            },
            {
              channel: Channels.INAPP_WEB,
              id: 'instant:batchingWindow',
              initialValue: (
                notification.options?.INAPP_WEB as InappWebDeliveryOptions
              ).instant?.batchingWindow,
              value: (
                updatedNotification.options
                  ?.INAPP_WEB as InappWebDeliveryOptions
              ).instant?.batchingWindow
            },
            {
              channel: Channels.INAPP_WEB,
              id: 'off.enabled',
              initialValue: (
                notification.options?.INAPP_WEB as InappWebDeliveryOptions
              ).off?.enabled,
              value: (
                updatedNotification.options
                  ?.INAPP_WEB as InappWebDeliveryOptions
              ).off?.enabled
            },

            // There was a typo that was pushed to production that resulted in 'defaultDeliverOption'. The below code handles those notifications as well
            {
              channel: Channels.INAPP_WEB,
              id: 'defaultDeliveryOption',
              initialValue:
                (notification.options?.INAPP_WEB as InappWebDeliveryOptions)
                  .defaultDeliveryOption ||
                (notification.options?.INAPP_WEB as InappWebDeliveryOptions)
                  .defaultDeliveryOption,
              value:
                (
                  updatedNotification.options
                    ?.INAPP_WEB as InappWebDeliveryOptions
                ).defaultDeliveryOption ||
                (notification.options?.INAPP_WEB as InappWebDeliveryOptions)
                  .defaultDeliveryOption
            }
          ];

    const getLabel = (
      id: Comparison['id'],
      value: Comparison['initialValue'] | Comparison['value']
    ): string | boolean | number | undefined => {
      if (id.endsWith('hour')) {
        return (
          (HOUR_OPTIONS.find((opt) => opt.value === value)?.label as string) ??
          value
        );
      } else if (id.endsWith('day')) {
        return (
          WEEKLY_OPTIONS.find((opt) => opt.value === value)?.label ?? value
        );
      } else if (id.endsWith('date')) {
        return (
          MONTHLY_OPTIONS.find((opt) => opt.value === value)?.label ?? value
        );
      } else if (id.endsWith('enabled')) {
        return value ? 'Enabled' : 'Disabled';
      } else {
        return String(value);
      }
    };

    const changedOptions = comparisons.filter(
      (comp) => comp.initialValue !== comp.value
    );

    const orderedLabels: { [key: string]: number } = {};
    Object.keys(CHANNEL_OPTION_LABELS).forEach(
      (label, i) => (orderedLabels[label] = i)
    );

    const emailOptions = changedOptions
      .filter((comp) => comp.channel === Channels.EMAIL)
      .sort((a, b) => orderedLabels[a.id] - orderedLabels[b.id]);
    const inapWebOptions = changedOptions
      .filter((comp) => comp.channel === Channels.INAPP_WEB)
      .sort((a, b) => orderedLabels[a.id] - orderedLabels[b.id]);

    return (
      <>
        {channel === Channels.EMAIL && (
          <>
            {emailOptions.length > 0 && <Divider>Review Changes</Divider>}
            {emailOptions.map((option) => {
              return (
                <Row justify="space-between" key={option.id}>
                  <Col>
                    {CHANNEL_OPTION_LABELS[Channels.EMAIL][option.id] ??
                      option.id}
                  </Col>
                  <Col>
                    <Text delete>
                      {getLabel(option.id, option.initialValue)}
                    </Text>{' '}
                    <ArrowRightOutlined /> {getLabel(option.id, option.value)}
                  </Col>
                </Row>
              );
            })}
          </>
        )}
        {channel === Channels.INAPP_WEB && (
          <>
            {inapWebOptions.length > 0 && <Divider>Review Changes</Divider>}
            {inapWebOptions.map((option) => {
              return (
                <Row justify="space-between" key={option.id}>
                  <Col>
                    {CHANNEL_OPTION_LABELS[Channels.INAPP_WEB][option.id] ??
                      option.id}
                  </Col>
                  <Col>
                    <Text delete>
                      {getLabel(option.id, option.initialValue)}
                    </Text>{' '}
                    <ArrowRightOutlined /> {getLabel(option.id, option.value)}
                  </Col>
                </Row>
              );
            })}
          </>
        )}
      </>
    );
  };

  return <div style={{ width: '100%' }}>{getModalText()}</div>;
};

export default ChannelOptionsConfirm;
